/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { generateActionSuccessText } from "~/tools/text";

export const ERROR_TEXT = {
  default: "Something went wrong",
  tryAgainDefault: "Ooooops, something went wrong, please try again",
  currencyRateRefresh: "Can't refresh currency rate",
  currencyRateDownload: "Can't download currency rate",
  scanInProgress: "Scan in progress, please try again later",
  rateNotFound: "Rate not found, cancelling",
  accessDenied: "Access denied",
  notImplemented: "Not implemented",
  failedFetchingDiscoveryRules: "Failed fetching Discovery Rules",
  failedUpdatingDiscoveryRules: "Failed updating Discovery Rules",
  failedAddingDiscoveryRule: "Failed adding Discovery Rule",
  throttledRecalculation: "Recalculation already in progress",
  failedRecalculation: "Failed to recalculate data, please try again later",
  failedSubmittingForm: "Failed to submit the form",
  failedFetchingEnvironments: "Failed to fetch environments",
  failedAddingAccount: "Failed to add account, make sure credentials are valid",
  failedUpdateAccountCredentials:
    "Failed to update account credentials, make sure credentials are valid",
  failedAddingBusinessUnit:
    "Business unit already selected, please select a different unit",
  failedSettingCostSplittingEnv:
    "No environment selected, please select at least one environment",
  failedKpiTargetArchival: "Failed to archive Target",
} as const;

export const SUCCESS_TEXT = {
  thresholdUpdated: generateActionSuccessText("Threshold")()("updated")(),
  thresholdAdded: generateActionSuccessText("Threshold")()("added")(),
  accountOnboarded: generateActionSuccessText("Account")()("onboarded")(),
  billingDataAdded: generateActionSuccessText("Billing data")()("added")(),
  billingDataUpdated: generateActionSuccessText("Billing data")()("updated")(),
  billingDataRemoved: generateActionSuccessText("Billing data")()("removed")(),
  formSubmitted: generateActionSuccessText("Form")()("submitted")(),
  budgetChanged: generateActionSuccessText("Budget")()("changed")(),
  copied: "Copied to clipboard",
  scanStarted: "Scan started",
  applicationRemoved: "Application removed",
  export:
    "We are preparing the report for you. It will land in your mailbox soon.",
  download: "Our report has been downloaded",
  applicationAdded: generateActionSuccessText("Application")()("added")(),
  accountAdded: generateActionSuccessText("Account")()("added")(),
  accountsRemoved: generateActionSuccessText("Accounts")()("removed")(),
  accountRemoved: generateActionSuccessText("Account")()("removed")(),
  accountUpdated: generateActionSuccessText("Account")()("updated")(),
  accountCredentialsUpdated:
    generateActionSuccessText("Account")("credentials")("updated")(),
  accountOwnerAdded: generateActionSuccessText("Account owner")()("added")(),
  integrationOwnerAdded:
    generateActionSuccessText("Integration owner")()("added")(),
  contactAdded: generateActionSuccessText("Contact")()("added")(),
  contactPersonAdded: generateActionSuccessText("Contact person")()("added")(),
  contactPersonUpdated:
    generateActionSuccessText("Contact person")()("updated")(),
  ownerAdded: generateActionSuccessText("Owner")()("added")(),
  ownerUpdated: generateActionSuccessText("Owner")()("updated")(),
  estateRecordsApproved:
    generateActionSuccessText("Estate records")("have")("approved")(),
  estateRecordsRejected:
    generateActionSuccessText("Estate records")("have")("rejected")(),
  environmentRemoved: generateActionSuccessText("Environment")()("removed")(),
  environmentRemovedFromOrgUnit: generateActionSuccessText("Environment")()(
    "removed"
  )("from Organization Unit"),
  sandboxCreated: generateActionSuccessText("Sandbox")()("created"),
  environmentAdded: generateActionSuccessText("Environment")()("added")(),
  environmentAddedToOrgUnit: generateActionSuccessText("Environment")()(
    "added"
  )("to Organization Unit"),
  mappingRuleAdded: generateActionSuccessText("New Mapping Rule")()("added")(),
  costSplittingChanged: generateActionSuccessText("Cost splitting algorithm")()(
    "changed"
  )(),
  envsAttached: generateActionSuccessText("Environments")()("attached")(),
  discoveryRulesChanged:
    generateActionSuccessText("Discovery Rules")("have")("changed")(),
  primaryBusinessContextSet: generateActionSuccessText(
    "Primary Business Context"
  )()("set")(),
  recalculationSuccess:
    generateActionSuccessText("Recalculation")()("triggered")(),
  orgUnitRemoved: generateActionSuccessText("Organization Unit")()("removed")(),
  kpiTargetArchived: generateActionSuccessText("Target")()("archived")(),
  schedulerSettingsApplied:
    generateActionSuccessText("Scheduler settings")("have")("applied")(),
  serviceNowSettingsApplied: generateActionSuccessText("ServiceNow settings")(
    "have"
  )("applied")(),
  emailSettingsApplied:
    generateActionSuccessText("Email settings")("have")("applied")(),
  slackSettingsApplied:
    generateActionSuccessText("Slack settings")("have")("applied")(),
} as const;

export const DEFAULT_SAVING_SUGGESTION =
  "Right-size or shutdown underutilized virtual machines";

export const NO_FORECAST_TEXT =
  "The forecast is not available yet. It usually takes a few days to recalculate.";
